import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";
import ListingEditModal from "../components/ListingEditModal";
import ListingHeaderBar from "../components/ListingHeaderBar";
import MediaGallery from "../components/MediaGallery";
import PropertyDetails from "../components/PropertyDetails";

/**
 * Read-only "top section" of the analysis for any listing (sale or rental): the same shared
 * title bar + media carousel + property details used on the analysis page. No comps / IRR /
 * checklist form. For sales it also fetches the rent prediction so PropertyDetails fills out.
 */
export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [prediction, setPrediction] = useState(null); // { property, prediction } — sales only
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const d = await authFetch(`/listings/${id}/detail`);
      setDetail(d);
      if (d?.sale_or_rental === "S") {
        try { setPrediction(await authFetch(`/predict/sale/${id}`)); }
        catch { setPrediction(null); /* sale may have no prediction yet */ }
      } else {
        setPrediction(null);
      }
    } catch {
      setError("Listing not found");
    } finally {
      setLoading(false);
    }
  };

  const pageStyle = { minHeight: "100vh", backgroundColor: "#f5f5f5" };
  const wrap = { padding: "20px", maxWidth: "1100px", margin: "0 auto" };

  if (loading) return (<div style={pageStyle}><Header showLogout={true} /><div style={{ ...wrap, textAlign: "center", marginTop: 40 }}>Loading…</div></div>);
  if (error || !detail) return (<div style={pageStyle}><Header showLogout={true} /><div style={{ ...wrap, textAlign: "center", marginTop: 40, color: "#c62828" }}>{error || "Not found"}</div></div>);

  const isSale = detail.sale_or_rental === "S";
  const pred = prediction?.prediction;

  // Prefer the rich prediction property (full media etc.) for sales; otherwise map the detail row.
  const prop = prediction?.property || {
    id: detail.id,
    address: detail.address,
    unit_number: detail.unit_number,
    verified: !!detail.verified_at,
    building_type: detail.condo_coop,
    listing_url: detail.listing_url,
    listing_status: detail.listing_status,
    price: detail.list_price,
    asking_price: detail.list_price,
    bedrooms: detail.bedrooms,
    bathrooms: detail.bathrooms,
    sqft: detail.sqft,
    neighborhood: detail.neighborhood,
    monthly_hoa: detail.hoa,
    monthly_tax: detail.tax,
    floor_plan_url: detail.floorplan_url_from_media,
    video_urls: detail.video_url_from_media ? [detail.video_url_from_media] : [],
    image_urls: [],
  };

  return (
    <div style={pageStyle}>
      <Header showLogout={true} />
      <div style={wrap}>
        <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* Back, not a link to /listings: going back is what restores the search the user
              ran (ListingSearchPage keeps it per tab). Fall back to the board when this page
              was opened straight from a URL and there is nothing to go back to. */}
          <span onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/listings"))}
            style={{ color: "#1976d2", cursor: "pointer", fontSize: 13 }}>‹ Back to listings</span>
          {isSale && pred?.predicted_rent && (
            // New tab: the analysis page is a destination of its own, and replacing this one
            // would leave the browser's Back button as the only way back to these details.
            <button onClick={() => window.open(`/predict/${id}`, "_blank")}
              style={{ padding: "6px 14px", backgroundColor: "#1976d2", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Open full analysis →
            </button>
          )}
        </div>

        {/* Same title bar + carousel + details as the analysis top section */}
        <ListingHeaderBar property={prop} onEdit={() => setEditing(true)} />
        <MediaGallery property={prop} />
        <PropertyDetails property={prop} prediction={pred} propertyAnalysis={null} checklistData={null} computedValues={undefined} />
      </div>

      {editing && (
        <ListingEditModal
          listing={{ id: Number(id) }}
          onClose={() => setEditing(false)}
          onSave={() => { setEditing(false); load(); }}
          onVerify={() => { setEditing(false); load(); }}
        />
      )}
    </div>
  );
}
